/**
 * Hook for managing delivery configuration
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { bitrix24DefaultFieldName } from '@aica/core';
import { useEmbeddedOrg } from '@/contexts/EmbeddedOrgContext';
import { useEmbeddedApiClient } from '@/hooks/useEmbeddedApiClient';
import { logError, logInfo } from '@/lib/logger';
import { USE_MOCK_DELIVERY_CONFIG } from '../constants';
import type {
  DeliveryConfig,
  DeliveryConfigResponse,
  FieldStatus,
  SmartProcess,
  DeliveryHistoryItem,
} from '../types';
import type { OrgDeliveryConfigResponse } from '@aica/api-types';
import {
  applyCreateFieldsResult,
  rebaseEditsOnSaved,
  toDeliveryPatchBody,
  toDeliveryTestBody,
  toDeliveryView,
  toFieldStatuses,
  toSavedSource,
  unknownFieldStatuses,
} from './deliveryConfigMapper';

const deliveryConfigQueryKey = (orgId: string | null) =>
  ['embedded', 'delivery-config', orgId] as const;
// Field statuses are their own query: a portal that cannot answer them must not
// take the destination and the switches down with it.
const deliveryFieldsQueryKey = (orgId: string | null) =>
  ['embedded', 'delivery-fields', orgId] as const;

export function useDeliveryConfig() {
  const { t } = useTranslation(['dashboard', 'common']);
  // Embedded Settings mounts EmbeddedOrgProvider, not RoleProvider — useRole()
  // would crash here (useOrganization throws without its provider). Source the
  // org id from the embedded session context instead.
  const { orgId: selectedOrgId } = useEmbeddedOrg();
  const { apiClient } = useEmbeddedApiClient();
  const queryClient = useQueryClient();

  // Loading states
  const [error, setError] = useState<string | null>(null);

  // Delivery configuration state
  const [config, setConfig] = useState<DeliveryConfig>({
    deliverToCrmCard: true,
    deliverToSmartProcess: false,
    smartProcessId: '',
    fields: {
      transcript: true,
      summary: true,
      qualityScore: true,
      saleProbability: true,
      keywords: false,
      sentiment: false,
    },
  });

  // The switches for the server state the tab last took in (`loadedFromRef`
  // below): what "unsaved" compares with. The same base the PATCH body is
  // computed against (#3025) — the two are only ever set together, or a
  // reverted edit would still look unsaved and send an empty PATCH reported as
  // saved.
  const [originalConfig, setOriginalConfig] = useState<DeliveryConfig | null>(null);

  // Additional data from API
  const [smartProcesses, setSmartProcesses] = useState<SmartProcess[]>([]);
  const [recentDeliveries, setRecentDeliveries] = useState<DeliveryHistoryItem[]>([]);

  // Action states
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [creatingFields, setCreatingFields] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [testStatus, setTestStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [testMessage, setTestMessage] = useState<string>('');

  // Reset state when organization changes (prevents stale data)
  useEffect(() => {
    setConfig({
      deliverToCrmCard: true,
      deliverToSmartProcess: false,
      smartProcessId: '',
      fields: {
        transcript: true,
        summary: true,
        qualityScore: true,
        saleProbability: true,
        keywords: false,
        sentiment: false,
      },
    });
    setOriginalConfig(null);
    // The hydration effect below may run in this same pass, for an organization
    // already cached, before the refs follow the state: it must not carry this
    // organization's unsaved edits into that one.
    originalConfigRef.current = null;
    loadedFromRef.current = null;
    setSmartProcesses([]);
    setRecentDeliveries([]);
    setError(null);
    setSaveStatus('idle');
    setTestStatus('idle');
    setTestMessage('');
  }, [selectedOrgId]);

  // Check if config has unsaved changes
  const isDirty =
    originalConfig && JSON.stringify(config) !== JSON.stringify(originalConfig);

  // The hydration effect below reads these without depending on them: a refetch
  // (field creation, Smart Process creation) must not overwrite switches the admin
  // has already changed.
  const configRef = useRef(config);
  configRef.current = config;
  const originalConfigRef = useRef(originalConfig);
  originalConfigRef.current = originalConfig;
  // The server state the switches were last hydrated from — what a save
  // compares them with (#3001); see `toDeliveryPatchBody`.
  const loadedFromRef = useRef<OrgDeliveryConfigResponse | null>(null);
  // The organization the tab shows now: a save that finishes after a switch to
  // another one must not write its result into that one's state.
  const orgIdRef = useRef(selectedOrgId);
  orgIdRef.current = selectedOrgId;

  // Cleanup timeout for saveStatus success message
  useEffect(() => {
    if (saveStatus === 'success') {
      const timeoutId = setTimeout(() => setSaveStatus('idle'), 3000);
      return () => clearTimeout(timeoutId);
    }
  }, [saveStatus]);

  // Cleanup timeout for testStatus success message
  useEffect(() => {
    if (testStatus === 'success') {
      const timeoutId = setTimeout(() => {
        setTestStatus('idle');
        setTestMessage('');
      }, 5000);
      return () => clearTimeout(timeoutId);
    }
  }, [testStatus]);

  // One GET-to-view mapping for the query and for a save's refetch.
  const fetchDeliveryView = async (): Promise<DeliveryConfigResponse> => {
    if (USE_MOCK_DELIVERY_CONFIG) {
      await new Promise((resolve) => setTimeout(resolve, 800));

      return {
        config: {
          deliverToCrmCard: true,
          deliverToSmartProcess: false,
          smartProcessId: '',
          fields: {
            transcript: true,
            summary: true,
            qualityScore: true,
            saleProbability: false,
            keywords: false,
            sentiment: false,
          },
        },
        smartProcesses: [
          {
            id: 'sp_1',
            title: t('embedded.mock.callAnalyticsTitle'),
            entityTypeId: 1034,
          },
          {
            id: 'sp_2',
            title: t('embedded.mock.salesConversationsTitle'),
            entityTypeId: 1035,
          },
        ],
        recentDeliveries: [
          {
            id: 'd1',
            recordId: 'rec_abc123',
            status: 'success',
            timestamp: t('common.minutesAgo', { ns: 'common', count: 2 }),
          },
          {
            id: 'd2',
            recordId: 'rec_def456',
            status: 'success',
            timestamp: t('common.minutesAgo', { ns: 'common', count: 15 }),
          },
          {
            id: 'd3',
            recordId: 'rec_ghi789',
            status: 'failed',
            timestamp: t('common.hoursAgo', { ns: 'common', count: 1 }),
            error: t('errors.notFound', { ns: 'common' }),
          },
        ],
        source: null,
      };
    }

    const deliveryConfig = await apiClient.settings.getDeliveryConfig(
      selectedOrgId as string
    );
    return toDeliveryView(deliveryConfig, t('embedded.callDataSmartProcess'));
  };

  const deliveryConfigQuery = useQuery({
    queryKey: deliveryConfigQueryKey(selectedOrgId),
    enabled: !!selectedOrgId,
    staleTime: 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: fetchDeliveryView,
  });

  const fieldsQuery = useQuery({
    queryKey: deliveryFieldsQueryKey(selectedOrgId),
    enabled: !!selectedOrgId,
    staleTime: 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<FieldStatus[]> => {
      if (USE_MOCK_DELIVERY_CONFIG) {
        await new Promise((resolve) => setTimeout(resolve, 800));
        return [
          {
            name: 'transcript',
            label: 'Transcript',
            created: true,
            fieldId: 'UF_CRM_AICA_TRANSCRIPT',
          },
          {
            name: 'summary',
            label: 'Summary',
            created: true,
            fieldId: 'UF_CRM_AICA_SUMMARY',
          },
          {
            name: 'qualityScore',
            label: t('embedded.qualityScore'),
            created: false,
          },
          {
            name: 'saleProbability',
            label: t('embedded.saleProbability'),
            created: false,
          },
          { name: 'keywords', label: 'Keywords', created: false },
          { name: 'sentiment', label: 'Sentiment', created: false },
        ];
      }

      return toFieldStatuses(await apiClient.settings.listBitrix24Fields());
    },
  });

  // Unknown, not "not created": a status that could not be read says nothing.
  // The rows stay so the switches stay editable; `fieldStatusesKnown` tells the
  // tab not to put a created / not-created badge on them.
  const fieldStatusesKnown = fieldsQuery.data !== undefined;
  const fieldStatuses: FieldStatus[] = fieldsQuery.data ?? unknownFieldStatuses();
  const fieldStatusesError = fieldsQuery.isError;

  useEffect(() => {
    if (!deliveryConfigQuery.error || !selectedOrgId) {
      return;
    }

    logError(
      t('embedded.logs.failedToLoadDeliveryConfig'),
      { component: 'useDeliveryConfig', orgId: selectedOrgId },
      deliveryConfigQuery.error instanceof Error ? deliveryConfigQuery.error : undefined
    );
    setError(
      deliveryConfigQuery.error instanceof Error
        ? deliveryConfigQuery.error.message
        : t('embedded.errors.failedToLoadConfiguration')
    );
  }, [selectedOrgId, deliveryConfigQuery.error, deliveryConfigQuery.errorUpdatedAt, t]);

  useEffect(() => {
    const data = deliveryConfigQuery.data;
    if (!data) {
      return;
    }

    setError(null);
    const apiConfig = data.config;
    const hydratedConfig: DeliveryConfig = {
      deliverToCrmCard: apiConfig.deliverToCrmCard,
      deliverToSmartProcess: apiConfig.deliverToSmartProcess,
      smartProcessId: apiConfig.smartProcessId ?? '',
      fields: apiConfig.fields,
    };
    // Hydrate the switches on first load and on every refetch (field creation,
    // Smart Process creation). Unsaved edits are carried over onto the newer
    // server state (#3025): a switch the admin changed keeps the admin's value,
    // an untouched one shows what the server holds now — and the base "unsaved"
    // compares with moves together with the one the PATCH body is computed
    // against, so a click is never overridden by a state the tab took in
    // earlier, and nothing the server already holds is reported unsaved.
    const original = originalConfigRef.current;
    setConfig(
      original === null
        ? hydratedConfig
        : rebaseEditsOnSaved(hydratedConfig, original, configRef.current)
    );
    setOriginalConfig(hydratedConfig);
    loadedFromRef.current = data.source;
    setSmartProcesses(data.smartProcesses);
    setRecentDeliveries(data.recentDeliveries);
  }, [deliveryConfigQuery.dataUpdatedAt, deliveryConfigQuery.data]);

  const loading = deliveryConfigQuery.isLoading;

  // Toggle field selection
  const toggleField = useCallback((field: keyof DeliveryConfig['fields']) => {
    setConfig((prev) => ({
      ...prev,
      fields: {
        ...prev.fields,
        [field]: !prev.fields[field],
      },
    }));
    setSaveStatus('idle');
  }, []);

  // The server stores ONE destination per provider, so the two destinations are
  // mutually exclusive: choosing one turns the other off. Turning the chosen one
  // off leaves none, which the tab reports and refuses to save.

  // Toggle CRM Card delivery
  const toggleCrmCard = useCallback(() => {
    setConfig((prev) =>
      prev.deliverToCrmCard
        ? { ...prev, deliverToCrmCard: false }
        : {
            ...prev,
            deliverToCrmCard: true,
            deliverToSmartProcess: false,
            smartProcessId: '',
          }
    );
    setSaveStatus('idle');
  }, []);

  // Toggle Smart Process delivery
  const toggleSmartProcess = useCallback(() => {
    setConfig((prev) =>
      prev.deliverToSmartProcess
        ? { ...prev, deliverToSmartProcess: false, smartProcessId: '' }
        : { ...prev, deliverToSmartProcess: true, deliverToCrmCard: false }
    );
    setSaveStatus('idle');
  }, []);

  // Update smartProcessId
  const updateSmartProcessId = useCallback((id: string) => {
    setConfig((prev) => ({
      ...prev,
      smartProcessId: id,
    }));
    setSaveStatus('idle');
  }, []);

  // Validation
  const isSmartProcessValid = !config.deliverToSmartProcess || !!config.smartProcessId;
  const hasSelectedFields = Object.values(config.fields).some(Boolean);
  const hasDestination = config.deliverToCrmCard || config.deliverToSmartProcess;
  const canSave = isDirty && hasSelectedFields && hasDestination && isSmartProcessValid;

  // Create Smart Process in Bitrix24
  const createSmartProcess = useCallback(async () => {
    try {
      const result = await apiClient.settings.createSmartProcess(selectedOrgId as string);
      if (result.success && result.entityTypeId) {
        setConfig((prev) => ({
          ...prev,
          deliverToSmartProcess: true,
          smartProcessId: result.entityTypeId,
        }));
        void queryClient.invalidateQueries({
          queryKey: deliveryConfigQueryKey(selectedOrgId),
        });
      }
      return result;
    } catch (err) {
      logError(
        'Failed to create smart process',
        { component: 'useDeliveryConfig' },
        err instanceof Error ? err : undefined
      );
      throw err;
    }
  }, [apiClient, selectedOrgId, queryClient]);

  // Create missing fields in Bitrix24
  const handleCreateFields = async () => {
    setCreatingFields(true);

    try {
      if (USE_MOCK_DELIVERY_CONFIG) {
        // Mock field creation
        await new Promise((resolve) => setTimeout(resolve, 2000));

        // Update field statuses
        queryClient.setQueryData<FieldStatus[]>(
          deliveryFieldsQueryKey(selectedOrgId),
          (previous) =>
            (previous ?? fieldStatuses).map((f) => ({
              ...f,
              created: config.fields[f.name as keyof DeliveryConfig['fields']]
                ? true
                : f.created,
              fieldId:
                config.fields[f.name as keyof DeliveryConfig['fields']] && !f.created
                  ? bitrix24DefaultFieldName(f.name)
                  : f.fieldId,
            }))
        );
      } else {
        // Real API call
        const response = await apiClient.settings.createBitrix24Fields(
          Object.keys(config.fields).filter(
            (f) => config.fields[f as keyof DeliveryConfig['fields']]
          )
        );
        // The server names what is ready on every card type; a field it
        // reports failed stays "not created" and is logged, not hidden.
        const updatedStatuses = applyCreateFieldsResult(fieldStatuses, response);
        if (response.failed.length > 0) {
          logError(
            t('embedded.logs.failedToCreateFields'),
            {
              component: 'useDeliveryConfig',
              failed: response.failed.map(
                (failure) => `${failure.name}@${failure.entityType}`
              ),
            },
            undefined
          );
        }
        // The statuses are their own query: updating them leaves the config
        // query, and with it the admin's unsaved switches, alone.
        queryClient.setQueryData<FieldStatus[]>(
          deliveryFieldsQueryKey(selectedOrgId),
          updatedStatuses
        );
      }

      logInfo(t('embedded.logs.fieldsCreatedInBitrix24'), {
        component: 'useDeliveryConfig',
      });
    } catch (err) {
      logError(
        t('embedded.logs.failedToCreateFields'),
        { component: 'useDeliveryConfig' },
        err instanceof Error ? err : undefined
      );
    } finally {
      setCreatingFields(false);
    }
  };

  // Save configuration
  const handleSave = async () => {
    if (!selectedOrgId) return;
    const orgId = selectedOrgId;
    const queryKey = deliveryConfigQueryKey(orgId);

    setSaving(true);
    setSaveStatus('idle');

    // The switches this save sends; an edit made while it is in flight is
    // carried over onto the saved state, not replaced by it.
    const submitted = config;
    // What the server holds after the save, as the tab shows it; `null` when
    // that could not be read.
    let savedView: DeliveryConfigResponse | null = null;
    let savedConfig = config;
    try {
      if (USE_MOCK_DELIVERY_CONFIG) {
        // Mock save
        await new Promise((resolve) => setTimeout(resolve, 1000));
      } else {
        // Real API call: only what this tab changed (#3001)
        const source = deliveryConfigQuery.data?.source;
        if (!source) throw new Error('Delivery settings are not loaded');
        const { stored } = await apiClient.settings.updateMultiProviderDeliveryConfig(
          orgId,
          toDeliveryPatchBody(source, config, loadedFromRef.current ?? source)
        );
        // A GET still in flight (a Smart Process creation's refetch, Retry)
        // carries the state from before this save: it must not land over it.
        await queryClient.cancelQueries({ queryKey, exact: true });
        // What the server holds now, this save merged in (#3025): a switch this
        // save did not send shows what another writer stored, not the value the
        // tab's GET held. An answer that does not parse — refetch, not a guess.
        savedView = stored
          ? toDeliveryView(
              toSavedSource(source, stored),
              t('embedded.callDataSmartProcess')
            )
          : await queryClient
              .fetchQuery({ queryKey, queryFn: fetchDeliveryView, staleTime: 0 })
              .catch((err: unknown) => {
                // The save itself is stored; only reading it back failed.
                logError(
                  t('embedded.logs.failedToLoadDeliveryConfig'),
                  { component: 'useDeliveryConfig', orgId },
                  err instanceof Error ? err : undefined
                );
                return null;
              });
        if (orgIdRef.current !== orgId) return;
        if (savedView) {
          // The whole view, the Smart Process list included: a Smart Process
          // another writer chose must be in the list the selector offers.
          savedConfig = savedView.config;
          loadedFromRef.current = savedView.source;
        }
      }

      const view = savedView;
      // `null` when the saved state could not be read back.
      let base: DeliveryConfig | null = savedConfig;
      if (view) {
        queryClient.setQueryData<DeliveryConfigResponse>(queryKey, (previous) =>
          previous ? view : previous
        );
      } else if (USE_MOCK_DELIVERY_CONFIG) {
        queryClient.setQueryData<DeliveryConfigResponse>(queryKey, (previous) =>
          previous ? { ...previous, config: savedConfig } : previous
        );
      } else {
        // The save is stored, but what the server holds now is unknown: no base
        // to compare with, so nothing is offered to save — a PATCH against a
        // guess could be empty and reported as saved, or resend another
        // writer's flag. The next GET brings the base back (hydration).
        base = null;
        loadedFromRef.current = null;
        void queryClient.invalidateQueries({ queryKey, exact: true });
      }
      setConfig((current) => rebaseEditsOnSaved(savedConfig, submitted, current));
      setOriginalConfig(base);
      setSaveStatus('success');
      logInfo(t('embedded.logs.deliveryConfigSaved'), {
        component: 'useDeliveryConfig',
        config: savedConfig,
      });
    } catch (err) {
      setSaveStatus('error');
      logError(
        t('embedded.logs.failedToSaveDeliveryConfig'),
        { component: 'useDeliveryConfig' },
        err instanceof Error ? err : undefined
      );
    } finally {
      setSaving(false);
    }
  };

  // Test connection
  const handleTest = async () => {
    if (!selectedOrgId) return;

    setTesting(true);
    setTestStatus('idle');
    setTestMessage('');

    try {
      if (USE_MOCK_DELIVERY_CONFIG) {
        // Mock test
        await new Promise((resolve) => setTimeout(resolve, 1500));
        setTestMessage(t('embedded.connectionSuccessfulTestDataSent'));
      } else {
        // Real API call
        const source = deliveryConfigQuery.data?.source;
        if (!source) throw new Error('Delivery settings are not loaded');
        const result = await apiClient.settings.testDelivery(
          selectedOrgId,
          toDeliveryTestBody(source, config)
        );
        setTestMessage(result.message || t('embedded.connectionSuccessful'));
      }

      setTestStatus('success');
      logInfo(t('embedded.logs.deliveryTestSuccessful'), {
        component: 'useDeliveryConfig',
      });
    } catch (err) {
      setTestStatus('error');
      setTestMessage(
        err instanceof Error ? err.message : t('embedded.connectionTestFailed')
      );
      logError(
        t('embedded.logs.deliveryTestFailed'),
        { component: 'useDeliveryConfig' },
        err instanceof Error ? err : undefined
      );
    } finally {
      setTesting(false);
    }
  };

  return {
    // State
    config,
    loading,
    error,
    smartProcesses,
    fieldStatuses,
    fieldStatusesKnown,
    fieldStatusesError,
    recentDeliveries,
    isDirty,
    canSave,
    hasDestination,
    // Action states
    saving,
    testing,
    creatingFields,
    saveStatus,
    testStatus,
    testMessage,
    // Actions
    toggleField,
    toggleCrmCard,
    toggleSmartProcess,
    updateSmartProcessId,
    createSmartProcess,
    handleCreateFields,
    handleSave,
    handleTest,
    refetch: async () => {
      if (!selectedOrgId) {
        return;
      }

      await queryClient.invalidateQueries({
        queryKey: deliveryConfigQueryKey(selectedOrgId),
        exact: true,
      });
      await queryClient.invalidateQueries({
        queryKey: deliveryFieldsQueryKey(selectedOrgId),
        exact: true,
      });
    },
    refetchFieldStatuses: async () => {
      if (!selectedOrgId) {
        return;
      }

      await queryClient.invalidateQueries({
        queryKey: deliveryFieldsQueryKey(selectedOrgId),
        exact: true,
      });
    },
  };
}
